// src/components/Auth.jsx
// Modernisiertes Login/Registrierungs-Interface mit Toast-Benachrichtigungen

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { supabase, supabaseConfigError } from '../supabaseClient';
import { useToast } from '../context/ToastContext';
import { validateEmail, validatePassword, validateName } from '../utils/validation';

import '../styles/base.css';
import '../styles/layout.css';
import '../styles/admin.css';

// ── Passwort-Sichtbarkeit Toggle (SVG-Icons) ─────────────────────────────────
const EyeIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

// ── Haupt-Auth-Komponente ─────────────────────────────────────────────────────
export default function Auth() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();
  const toast = useToast();

  // Hintergrundklasse für Home-Seite setzen
  useEffect(() => {
    document.body.classList.add('home');
    return () => document.body.classList.remove('home');
  }, []);

  if (supabaseConfigError) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h2 className="auth-title">Konfiguration fehlt</h2>
          <p className="auth-error-box">
            Supabase ist nicht konfiguriert. Setze die Variablen
            <code> VITE_SUPABASE_URL</code> und
            <code> VITE_SUPABASE_ANON_KEY</code> in der Datei <code>.env</code>
            (Vorlage: <code>.env.example</code>).
          </p>
        </div>
      </div>
    );
  }

  // ── Registrierung ───────────────────────────────────────────────────────────
  const handleSignUp = async (e) => {
    e.preventDefault();
    setError(null);

    const nameCheck = validateName(name);
    if (!nameCheck.valid) {
      setError(nameCheck.error);
      return;
    }

    const emailCheck = validateEmail(email);
    if (!emailCheck.valid) {
      setError(emailCheck.error);
      return;
    }

    const passwordCheck = validatePassword(password);
    if (!passwordCheck.valid) {
      setError(passwordCheck.error);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        // Keine Rolle mitgeben: user_metadata ist clientseitig beschreibbar.
        // Rollen vergibt ausschließlich set_user_role() nach app_metadata.
        options: { data: { name } },
      });

      if (error) {
        setError(error.message);
        return;
      }

      const user = data?.user;
      if (!user) {
        setError('Registrierung konnte nicht abgeschlossen werden. Bitte versuche es erneut.');
        return;
      }

      if (data.session) {
        toast.success('Registrierung erfolgreich! Willkommen, ' + name + '!');
        navigate('/character');
        return;
      }

      const { error: loginError } = await supabase.auth.signInWithPassword({ email, password });
      if (loginError) {
        toast.info('Registrierung erfolgreich! Bitte logge dich mit deinen Zugangsdaten ein.');
        setIsSignUp(false);
        return;
      }

      toast.success('Registrierung erfolgreich! Willkommen, ' + name + '!');
      navigate('/character');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Login ────────────────────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);

    const emailCheck = validateEmail(email);
    if (!emailCheck.valid) {
      setError(emailCheck.error);
      return;
    }
    if (!password) {
      setError('Bitte Passwort eingeben.');
      return;
    }

    setIsLoading(true);
    try {
      const { data, error: loginError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (loginError) {
        setError(loginError.message);
        return;
      }

      if (data?.user) {
        // Vollbildmodus versuchen (ignoriert Fehler, da Browser-Policy variiert)
        try {
          const el = document.documentElement;
          if (el.requestFullscreen) await el.requestFullscreen();
          else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
        } catch {
          /* Vollbild optional */
        }

        navigate('/character');
      } else {
        setError('Login fehlgeschlagen. Bitte Zugangsdaten prüfen.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // ── JSX ──────────────────────────────────────────────────────────────────────
  return (
    <div className="auth-container">
      <div className="auth-card">
        {/* Logo / Titel */}
        <div className="auth-header">
          <div className="auth-logo" aria-hidden="true"></div>
          <h1 className="auth-title">{isSignUp ? 'Abenteurer registrieren' : 'Anmelden'}</h1>
          <p className="auth-subtitle">
            {isSignUp ? 'Erstelle dein Abenteurerkonto' : 'Willkommen in der DSA 4.1 Web App'}
          </p>
        </div>

        {/* Fehlermeldung */}
        {error && (
          <div className="auth-error-box" role="alert">
            {error}
          </div>
        )}

        {/* Formular */}
        <form className="auth-form" onSubmit={isSignUp ? handleSignUp : handleLogin} noValidate>
          {isSignUp && (
            <div className="auth-field">
              <label htmlFor="auth-name" className="auth-label">
                Name
              </label>
              <input
                id="auth-name"
                type="text"
                className="auth-input"
                placeholder="Dein Abenteurername"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                required
              />
            </div>
          )}

          <div className="auth-field">
            <label htmlFor="auth-email" className="auth-label">
              E-Mail
            </label>
            <input
              id="auth-email"
              type="email"
              className="auth-input"
              placeholder="held@abenteuer.de"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </div>

          <div className="auth-field">
            <label htmlFor="auth-password" className="auth-label">
              Passwort
            </label>
            <div className="auth-password-wrap">
              <input
                id="auth-password"
                type={showPassword ? 'text' : 'password'}
                className="auth-input auth-input--password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={isSignUp ? 'new-password' : 'current-password'}
                required
              />
              <button
                type="button"
                className="auth-password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
                aria-pressed={showPassword}
                tabIndex={-1}
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </div>

          <button
            id="auth-submit"
            className="button auth-submit"
            type="submit"
            disabled={isLoading}
            aria-busy={isLoading}
          >
            {isLoading
              ? isSignUp
                ? 'Registriere...'
                : 'Anmelden...'
              : isSignUp
                ? 'Registrieren'
                : 'Anmelden'}
          </button>
        </form>

        {/* Umschalten */}
        <p className="auth-switch">
          {isSignUp ? 'Bereits registriert? ' : 'Noch kein Konto? '}
          <button
            className="auth-switch-link"
            onClick={() => {
              // Beim Wechsel zwischen Login und Registrierung faengt die Maske
              // frisch an. Frueher machte das ein Effekt auf isSignUp.
              setIsSignUp((aktuell) => !aktuell);
              setError(null);
              setShowPassword(false);
            }}
            type="button"
          >
            {isSignUp ? 'Hier einloggen' : 'Hier registrieren'}
          </button>
        </p>
      </div>
    </div>
  );
}
